import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Gift,
  Search,
  Filter,
  Star,
  X,
  ShieldCheck,
  QrCode,
  ArrowLeft,
  Home,
  History,
  User,
  ArrowRight,
  Coffee,
  Utensils,
  Zap,
  CheckCircle2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { getRewardsCatalog, getClientPoints, clearMessage } from '../Redux/Actions/ClientActions';
import logo from '../image/LOGKDO+.png';
import './Rewards.css';

// Helper function to build image URL
const getImageUrl = (url) => {
  if (!url) return null;
  // Verify if it's a Base64 string or an external URL
  if (url.startsWith('data:') || url.startsWith('http')) {
    return url;
  }
  // Fallback for potentially older relative paths (though we are migrating to Base64)
  // or return null to trigger the placeholder
  return null;
};

// --- Internal Component: RewardCard ---
const RewardCard = ({ reward, points, onShowQR }) => {
  const pointsNeeded = reward.pointsRequired || 0;
  const progress = Math.min((points / (pointsNeeded || 1)) * 100, 100);
  const canAfford = points >= pointsNeeded;

  return (
    <div
      className={`mobile-reward-card animate-in group relative ${canAfford ? 'cursor-pointer' : ''}`}
      onClick={() => canAfford && onShowQR(reward)}
    >
      <div className="card-image-wrapper">
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent z-10"></div>

        {reward.imageUrl ? (
          <img
            src={getImageUrl(reward.imageUrl)}
            alt={reward.name}
            onError={(e) => { e.target.src = 'https://via.placeholder.com/400x200?text=Reward'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-slate-100">
            <Gift size={48} className="text-slate-300" />
          </div>
        )}

        {canAfford && (
          <div className="status-badge-available">
            <CheckCircle2 size={12} /> Disponible
          </div>
        )}

        <div className="card-points-overlay">
          <div className="points-pill-vibrant">
            <Star size={14} className="star-icon" />
            <span>{pointsNeeded} PTS</span>
          </div>
        </div>
      </div>

      <div className="card-info-content">
        <h3 className="card-title">{reward.name}</h3>
        <p className="card-desc">{reward.description}</p>

        {!canAfford ? (
          <div className="progress-container">
            <div className="progress-header">
              <span className="progress-label">Progression</span>
              <span className="progress-value">{Math.round(progress)}%</span>
            </div>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
          </div>
        ) : (
          <div className="card-action-row">
            <span className="action-hint">Prêt à échanger !</span>
            <div className="redeem-trigger-btn">
              <ArrowRight size={20} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Main Component: Rewards ---
const Rewards = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Correction des sélecteurs
  const { points = 0, client = {} } = useSelector(state => state.clientReducer) || {};
  const { rewards = [], loading: catalogLoading } = useSelector(state => state.pointsReducer) || {};

  const [filterType, setFilterType] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);
  const [qrCodeString, setQrCodeString] = useState(null);
  // --- Horizontal Draggable Scroll Logic ---
  const useDraggableScroll = (ref) => {
    const isDown = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);

    const onMouseDown = (e) => {
      isDown.current = true;
      ref.current.classList.add('active');
      startX.current = e.pageX - ref.current.offsetLeft;
      scrollLeft.current = ref.current.scrollLeft;
    };

    const onMouseLeave = () => {
      isDown.current = false;
      ref.current?.classList.remove('active');
    };

    const onMouseUp = () => {
      isDown.current = false;
      ref.current?.classList.remove('active');
    };

    const onMouseMove = (e) => {
      if (!isDown.current) return;
      e.preventDefault();
      const x = e.pageX - ref.current.offsetLeft;
      const walk = (x - startX.current) * 2; // scroll-fast scale
      ref.current.scrollLeft = scrollLeft.current - walk;
    };

    return { onMouseDown, onMouseLeave, onMouseUp, onMouseMove };
  };

  const scrollRef = useRef(null);
  const dragEvents = useDraggableScroll(scrollRef);

  useEffect(() => {
    dispatch(getRewardsCatalog());
    if (client && client._id) {
      dispatch(getClientPoints(client._id));
    }
  }, [dispatch, client?._id]);

  const handleShowQR = (reward) => {
    setSelectedReward(reward);

    // FORMAT ATTENDU PAR LE BACKEND POUR SCAN-PENDING (DANS RewardController.js)
    // Format: REWARD_PENDING:uniqueCode:rewardId:clientEmail:points
    const uniqueCode = `RWD-${Date.now()}`;
    const qrString = `REWARD_PENDING:${uniqueCode}:${reward._id}:${client.email}:${reward.pointsRequired}`;

    setQrCodeString(qrString);
    setShowModal(true);
    // REMARQUE : On ne réduit AUCUN point ici. La réduction se fait quand l'admin scanne.
  };

  // Dynamic Categories
  const uniqueCategories = ['all', ...new Set(rewards.map(r => r.category).filter(Boolean))];

  // Configuration des images de catégories
  const categoryImages = {
    'all': 'https://images.unsplash.com/photo-1512909006721-3d6018887383?auto=format&fit=crop&q=80&w=150&h=150', // Cadeaux divers
    'boissons': 'https://images.unsplash.com/photo-1544145945-f90425340c7e?auto=format&fit=crop&q=80&w=150&h=150', // Boissons
    'cuisine': 'https://images.unsplash.com/photo-1556910103-1c02745a30bf?auto=format&fit=crop&q=80&w=150&h=150', // Cuisine
    'food': 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&q=80&w=150&h=150', // Food
    'statut': 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&q=80&w=150&h=150', // VIP/Status
    'default': 'https://images.unsplash.com/photo-1513885535751-8b9238bd345a?auto=format&fit=crop&q=80&w=150&h=150'
  };

  const getCategoryImage = (cat) => {
    const lowerKey = cat.toLowerCase();

    // 1. Check hardcoded mappings first
    if (categoryImages[lowerKey]) return categoryImages[lowerKey];
    if (lowerKey.includes('boiss') || lowerKey.includes('drink')) return categoryImages['boissons'];
    if (lowerKey.includes('manger') || lowerKey.includes('food') || lowerKey.includes('cuis')) return categoryImages['cuisine'];
    if (lowerKey.includes('statut') || lowerKey.includes('vip')) return categoryImages['statut'];

    // 2. Dynamic Fallback: Find first reward with this category and use its image
    const matchingReward = rewards.find(r => r.category === cat && r.imageUrl);
    if (matchingReward) {
      return getImageUrl(matchingReward.imageUrl);
    }

    // 3. Default fallback
    return categoryImages['default'];
  };

  const filteredRewards = filterType === 'all'
    ? rewards
    : rewards.filter(r => r.category && r.category === filterType);

  return (
    <div className="mobile-app-container">
      {/* iOS Style Header */}
      <header className="mobile-native-header">
        <div className="header-main-content">
          <div className="header-stacked-text">
            <img src={logo} alt="LOGKDO+" className="header-brand-logo" />
            <h1 className="header-title-premium">Cadeaux</h1>
          </div>
          <div className="header-points-badge" onClick={() => navigate('/history')}>
            <div className="points-icon-wrapper">
              <Star size={14} className="fill-primary" />
            </div>
            <div className="points-text-wrapper">
              <span className="points-amount">{points?.toLocaleString() || 0}</span>
              <span className="points-label">PTS</span>
            </div>
          </div>
        </div>

        {/* Categories with Scroll Arrows */}
        <div className="categories-wrapper">
          <div
            className="mobile-category-bar"
            ref={scrollRef}
            {...dragEvents}
          >
            {uniqueCategories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilterType(cat)}
                className={`category-sq-btn ${filterType === cat ? 'is-active' : ''}`}
                style={{
                  backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.5)), url(${getCategoryImage(cat)})`
                }}
              >
                <span className="cat-sq-label">{cat === 'all' ? 'Tout' : cat}</span>
                {filterType === cat && <div className="cat-sq-indicator"></div>}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Blue Separator Line */}
      <hr className="border-t border-blue-200 my-0" />

      <main className="mobile-main-feed">
        {catalogLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="native-spinner"></div>
            <p className="mt-4 text-slate-400 font-medium">Chargement des offres...</p>
          </div>
        ) : (
          <div className="mobile-rewards-grid">
            {filteredRewards.length > 0 ? (
              filteredRewards.map((reward) => (
                <RewardCard
                  key={reward._id}
                  reward={reward}
                  points={points}
                  onShowQR={handleShowQR}
                />
              ))
            ) : (
              <div className="mobile-empty-state text-center py-20 col-span-2">
                <Gift size={64} className="mx-auto text-slate-200 mb-4" strokeWidth={1} />
                <h3 className="text-xl font-bold">Aucun cadeau</h3>
                <p className="text-slate-400">Revenez plus tard ou changez de catégorie.</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Bottom Sheet Modal - POUR LE SCAN PAR L'ADMIN */}
      {showModal && (
        <div className="mobile-bottom-sheet-overlay" onClick={() => setShowModal(false)}>
          <div className="mobile-bottom-sheet" onClick={e => e.stopPropagation()}>
            <div className="sheet-grabber"></div>

            <div className="sheet-header">
              <h2 className="sheet-title">Échanger</h2>
              <button className="sheet-close-btn" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="sheet-body">
              <div className="sheet-reward-preview">
                {selectedReward?.imageUrl ? (
                  <img src={getImageUrl(selectedReward.imageUrl)} alt={selectedReward?.name} />
                ) : (
                  <div className="w-[70px] h-[70px] bg-slate-200 rounded-xl flex items-center justify-center">
                    <Gift size={32} className="text-slate-400" />
                  </div>
                )}
                <div className="sheet-reward-info">
                  <h3>{selectedReward?.name}</h3>
                  <div className="sheet-cost-tag">
                    <Star size={14} className="fill-accent" />
                    <span>{selectedReward?.pointsRequired} Points</span>
                  </div>
                </div>
              </div>

              <div className="sheet-qr-display">
                <p className="text-slate-500 text-sm mb-6 leading-relaxed">
                  Présentez ce code à l'administrateur <br />
                  <span className="font-bold text-primary">pour valider votre cadeau</span>
                </p>

                <div className="sheet-qr-container">
                  <div className="qr-scan-line"></div>
                  <img
                    className="qr-code-image"
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qrCodeString)}`}
                    alt="QR Code"
                  />
                </div>

                <div className="mt-6 flex items-center gap-2 justify-center text-green-600 font-bold bg-green-50 py-3 rounded-2xl border border-green-100">
                  <CheckCircle2 size={18} />
                  <span className="text-sm">QR Code actif</span>
                </div>
              </div>

              <button className="sheet-primary-btn" onClick={() => setShowModal(false)}>
                Terminer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shared Floating Bottom Nav */}
      <nav className="mobile-floating-nav">
        <button className="nav-btn" onClick={() => navigate('/my-points')}>
          <Home size={22} />
          <span>Accueil</span>
        </button>
        <button className="nav-btn is-active">
          <div className="nav-active-dot"></div>
          <Gift size={22} />
          <span>Cadeaux</span>
        </button>
        <button className="nav-btn" onClick={() => navigate('/scan-points')}>
          <div className="nav-center-scan">
            <QrCode size={24} color="white" />
          </div>
        </button>
        <button className="nav-btn" onClick={() => navigate('/history')}>
          <History size={22} />
          <span>Activités</span>
        </button>
        <button className="nav-btn" onClick={() => navigate('/profile')}>
          <User size={22} />
          <span>Profil</span>
        </button>
      </nav>
    </div>
  );
};

export default Rewards;